import {
  buildExportCsv,
  downloadFile,
  CSV_HEADERS_V1,
  CSV_HEADERS_V2,
  CSV_LIST_SEPARATOR,
  CSV_DEFAULT_MARKER,
} from "./reportTemplateExport.js";

export const MAX_IMPORT_TEMPLATES = 500;

export const SUPPORTED_VERSION = 2;

export const CSV_TEMPLATE_FILENAME = "report-template-import-template.csv";

const SAMPLE_TEMPLATE = {
  name: "Complete Blood Count",
  code: "CBC",
  reportKind: "PARAMETERIZED",
  investigationType: { name: "Pathology", code: "PATHOLOGY" },
  test: { name: "Complete Blood Count" },
  isActive: true,
  isDefault: false,
  parameters: [
    {
      name: "Hemoglobin",
      unit: "g/dL",
      normalRangeText: "12-17",
      normalRangeMin: 12,
      normalRangeMax: 17,
      fieldType: "NUMBER",
      required: true,
      displayOrder: 1,
      referenceRanges: [
        {
          gender: "MALE",
          normalRangeText: "13-17",
          normalRangeMin: 13,
          normalRangeMax: 17,
        },
        {
          gender: "FEMALE",
          normalRangeText: "12-15",
          normalRangeMin: 12,
          normalRangeMax: 15,
        },
      ],
    },
    {
      name: "Cholesterol",
      unit: "mg/dL",
      normalRangeText: "< 200",
      normalRangeMin: 0,
      normalRangeMax: 200,
      fieldType: "NUMBER",
      required: false,
      displayOrder: 2,
      methods: [
        { method: { name: "CHOD-POD" }, isDefault: true },
        { method: { name: "GPO Method" }, isDefault: false },
      ],
    },
    {
      name: "MCV",
      unit: "fL",
      fieldType: "NUMBER",
      required: false,
      displayOrder: 3,
      calculation: { name: "MCV" },
    },
    {
      name: "HIV I & II",
      fieldType: "SELECT",
      required: false,
      displayOrder: 4,
      // Dropdown values, separated with "|".
      options: ["Reactive", "Non-Reactive"],
    },
  ],
};

export const buildCsvTemplateSample = () => buildExportCsv([SAMPLE_TEMPLATE]);

export const downloadCsvTemplate = () =>
  downloadFile(
    buildCsvTemplateSample(),
    CSV_TEMPLATE_FILENAME,
    "text/csv;charset=utf-8;",
  );

export const CSV_COLUMN_NOTES = [
  "parameterMaleRange / Min / Max and parameterFemaleRange / Min / Max — optional gender-specific ranges. The existing parameterReferenceRange / Min / Max columns stay the default range for everyone.",
  `parameterMethods — Method Master names separated by "${CSV_LIST_SEPARATOR}". Add "${CSV_DEFAULT_MARKER}" to the default one, e.g. CHOD-POD${CSV_DEFAULT_MARKER}${CSV_LIST_SEPARATOR}GPO Method.`,
  "parameterCalculation — a Calculation Master name. The formula itself always stays in the Calculation Master.",
  `parameterOptions — dropdown values separated by "${CSV_LIST_SEPARATOR}" (used only when parameterInputType is SELECT).`,
  "Methods and calculations are matched by name in this database; unknown or inactive ones fail only their own row.",
];

const toNumOrNull = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const parseBool = (v, fallback) => {
  if (v === undefined || v === null) return fallback;
  if (typeof v === "boolean") return v;
  const s = v.toString().trim().toLowerCase();
  if (["true", "1", "yes", "y"].includes(s)) return true;
  if (["false", "0", "no", "n"].includes(s)) return false;
  return fallback;
};

const GENDERS = ["MALE", "FEMALE"];

const sanitizeReferenceRanges = (ranges) =>
  (Array.isArray(ranges) ? ranges : [])
    .filter((range) => GENDERS.includes(range?.gender))
    .map((range) => ({
      gender: range.gender,
      normalRangeText: (range?.normalRangeText ?? "").toString(),
      normalRangeMin: toNumOrNull(range?.normalRangeMin),
      normalRangeMax: toNumOrNull(range?.normalRangeMax),
    }));

const sanitizeMethods = (methods) =>
  (Array.isArray(methods) ? methods : [])
    .map((entry) => ({
      name: (entry?.name ?? "").toString().trim(),
      isDefault: Boolean(entry?.isDefault),
    }))
    .filter((entry) => entry.name);

const sanitizeOptions = (options) =>
  (Array.isArray(options) ? options : [])
    .map((option) => (option ?? "").toString().trim())
    .filter(Boolean);

const sanitizeCalculation = (calculation) => {
  const name = (calculation?.name ?? "").toString().trim();
  return name ? { name } : null;
};

const sanitizeParam = (p, i) => {
  const param = {
    name: (p?.name ?? "").toString(),
    unit: (p?.unit ?? "").toString(),
    normalRangeText: (p?.normalRangeText ?? "").toString(),
    normalRangeMin: toNumOrNull(p?.normalRangeMin),
    normalRangeMax: toNumOrNull(p?.normalRangeMax),
    required: Boolean(p?.required),
    displayOrder: Number.isFinite(Number(p?.displayOrder))
      ? Number(p?.displayOrder)
      : i,
  };
  if (p?.fieldType) param.fieldType = p.fieldType.toString();

  if (p?.referenceRanges !== undefined) {
    param.referenceRanges = sanitizeReferenceRanges(p.referenceRanges);
  }
  if (p?.methods !== undefined) param.methods = sanitizeMethods(p.methods);
  if (p?.calculation !== undefined) {
    param.calculation = sanitizeCalculation(p.calculation);
  }
  if (p?.options !== undefined) param.options = sanitizeOptions(p.options);

  return param;
};

export const sanitizeTemplateForImport = (t) => {
  const itCode = (t?.investigationType?.code ?? "").toString().trim();
  const itName = (t?.investigationType?.name ?? "").toString().trim();
  const testName = (t?.test?.name ?? "").toString().trim();

  const out = {
    name: (t?.name ?? "").toString(),
    investigationType:
      itCode || itName ? { name: itName, code: itCode } : null,
    test: testName ? { name: testName } : null,
    parameters: Array.isArray(t?.parameters)
      ? t.parameters.map(sanitizeParam)
      : [],
    isActive: t?.isActive !== false,
    isDefault: Boolean(t?.isDefault),
  };

  const code = (t?.code ?? "").toString().trim();
  if (code) out.code = code;
  if (t?.reportKind) out.reportKind = t.reportKind.toString();
  return out;
};

export const parseJsonTemplates = (text) => {
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error("This file is not valid JSON.");
  }

  if (Array.isArray(data)) return data;

  if (data && typeof data === "object") {
    if (
      data.version !== undefined &&
      Number(data.version) > SUPPORTED_VERSION
    ) {
      throw new Error(
        `Unsupported export version "${data.version}". This app supports version ${SUPPORTED_VERSION}. Please update before importing.`,
      );
    }
    if (Array.isArray(data.templates)) return data.templates;
  }

  throw new Error('JSON must be an export object containing a "templates" array.');
};

export const tokenizeCsv = (input) => {
  const text = (input || "").replace(/^﻿/, "");
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
      continue;
    }

    if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else if (c === "\r") {
      if (text[i + 1] === "\n") continue;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += c;
    }
  }
  row.push(field);
  rows.push(row);
  return rows;
};

const TEMPLATE_LEVEL_KEYS = [
  "name",
  "code",
  "itCode",
  "itName",
  "testName",
  "reportKind",
  "isActive",
  "isDefault",
];

const normalizeLevel = (lvl) => ({
  name: lvl.name.trim(),
  code: lvl.code.trim(),
  itCode: lvl.itCode.trim(),
  itName: lvl.itName.trim(),
  testName: lvl.testName.trim(),
  reportKind: lvl.reportKind.trim(),
  isActive: parseBool(lvl.isActive, true),
  isDefault: parseBool(lvl.isDefault, false),
});

export const decodeMethodsCell = (value) =>
  (value ?? "")
    .toString()
    .split(CSV_LIST_SEPARATOR)
    .map((token) => token.trim())
    .filter(Boolean)
    .map((token) => {
      const isDefault = token.endsWith(CSV_DEFAULT_MARKER);
      return {
        name: (isDefault ? token.slice(0, -1) : token).trim(),
        isDefault,
      };
    })
    .filter((entry) => entry.name);

export const decodeOptionsCell = (value) =>
  (value ?? "")
    .toString()
    .split(CSV_LIST_SEPARATOR)
    .map((token) => token.trim())
    .filter(Boolean);

const buildGenderRanges = (cells) =>
  GENDERS.map((gender) => {
    const prefix = gender === "MALE" ? "Male" : "Female";
    const text = cells[`parameter${prefix}Range`] ?? "";
    const min = cells[`parameter${prefix}Min`] ?? "";
    const max = cells[`parameter${prefix}Max`] ?? "";
    if (![text, min, max].some((v) => v.toString().trim() !== "")) return null;
    return {
      gender,
      normalRangeText: text,
      normalRangeMin: min,
      normalRangeMax: max,
    };
  }).filter(Boolean);

export const parseCsvTemplates = (text) => {
  const rows = tokenizeCsv(text).filter((r) =>
    r.some((c) => (c ?? "").toString().trim() !== ""),
  );
  if (rows.length < 2) {
    throw new Error("The CSV file has a header but no data rows.");
  }

  const header = rows[0].map((h) => (h ?? "").toString().trim());
  const idx = {};
  [...CSV_HEADERS_V1, ...CSV_HEADERS_V2].forEach((h) => {
    idx[h] = header.indexOf(h);
  });
  const missing = CSV_HEADERS_V1.filter((h) => idx[h] === -1);
  if (missing.length) {
    throw new Error(
      `CSV headers don't match the Report Template export format. Missing column(s): ${missing.join(", ")}.`,
    );
  }

  const hasV2Columns = CSV_HEADERS_V2.some((h) => idx[h] !== -1);
  const cell = (row, key) =>
    idx[key] === -1 ? "" : (row[idx[key]] ?? "").toString();

  const order = [];
  const byIdentity = new Map();

  for (let r = 1; r < rows.length; r += 1) {
    const row = rows[r];
    if (row.length !== header.length) {
      throw new Error(
        `Malformed CSV: row ${r + 1} has ${row.length} columns but the header has ${header.length}.`,
      );
    }

    const lvl = {
      name: cell(row, "templateName"),
      code: cell(row, "templateCode"),
      itCode: cell(row, "investigationTypeCode"),
      itName: cell(row, "investigationTypeName"),
      testName: cell(row, "testName"),
      reportKind: cell(row, "reportKind"),
      isActive: cell(row, "isActive"),
      isDefault: cell(row, "isDefault"),
    };

    const identity = lvl.code.trim() || lvl.name.trim();
    if (!identity) {
      throw new Error(
        `Malformed CSV: row ${r + 1} has no template name or code.`,
      );
    }

    let entry = byIdentity.get(identity);
    if (!entry) {
      entry = {
        firstRow: r + 1,
        norm: normalizeLevel(lvl),
        template: {
          name: lvl.name,
          code: lvl.code,
          reportKind: lvl.reportKind,
          investigationType:
            lvl.itCode || lvl.itName
              ? { name: lvl.itName, code: lvl.itCode }
              : null,
          test: lvl.testName ? { name: lvl.testName } : null,
          isActive: parseBool(lvl.isActive, true),
          isDefault: parseBool(lvl.isDefault, false),
          parameters: [],
        },
      };
      byIdentity.set(identity, entry);
      order.push(identity);
    } else {
      const nextNorm = normalizeLevel(lvl);
      const diff = TEMPLATE_LEVEL_KEYS.find(
        (k) => entry.norm[k] !== nextNorm[k],
      );
      if (diff) {
        throw new Error(
          `Malformed CSV: template "${identity}" has conflicting "${diff}" values (rows ${entry.firstRow} and ${r + 1}). Every row of a template must share the same template-level values.`,
        );
      }
    }

    const pName = cell(row, "parameterName").trim();
    const pUnit = cell(row, "parameterUnit");
    const pRange = cell(row, "parameterReferenceRange");
    const pMin = cell(row, "parameterMin");
    const pMax = cell(row, "parameterMax");
    const pType = cell(row, "parameterInputType");
    const pReq = cell(row, "parameterRequired");
    const pOrder = cell(row, "parameterOrder");

    const v2Cells = {};
    CSV_HEADERS_V2.forEach((key) => {
      v2Cells[key] = cell(row, key);
    });

    const hasParamData = [
      pUnit,
      pRange,
      pMin,
      pMax,
      pType,
      pReq,
      pOrder,
      ...Object.values(v2Cells),
    ].some((v) => v.toString().trim() !== "");

    if (!pName) {
      if (hasParamData) {
        throw new Error(
          `Malformed CSV: row ${r + 1} has parameter data but no parameter name.`,
        );
      }
      continue;
    }

    const parameter = {
      name: pName,
      unit: pUnit,
      normalRangeText: pRange,
      normalRangeMin: pMin,
      normalRangeMax: pMax,
      fieldType: pType,
      required: parseBool(pReq, false),
      displayOrder: pOrder,
    };

    if (hasV2Columns) {
      parameter.referenceRanges = buildGenderRanges(v2Cells);
      parameter.methods = decodeMethodsCell(v2Cells.parameterMethods);
      parameter.calculation = v2Cells.parameterCalculation.trim()
        ? { name: v2Cells.parameterCalculation.trim() }
        : null;
      parameter.options = decodeOptionsCell(v2Cells.parameterOptions);
    }

    entry.template.parameters.push(parameter);
  }

  return order.map((id) => byIdentity.get(id).template);
};

const extensionOf = (filename) =>
  (filename || "").split(".").pop().toLowerCase();

export const parseImportFile = (filename, text) => {
  const ext = extensionOf(filename);
  let templates;
  if (ext === "json") {
    templates = parseJsonTemplates(text);
  } else if (ext === "csv") {
    templates = parseCsvTemplates(text);
  } else {
    throw new Error("Unsupported file type. Choose a .json or .csv file.");
  }

  if (!Array.isArray(templates) || templates.length === 0) {
    throw new Error("No templates were found in the file.");
  }

  return templates.map(sanitizeTemplateForImport);
};
